import React, { useState, useEffect, useMemo, useCallback } from "react";
import { X, Plus, Upload, FileText, Trash2, Edit, Search, CheckCircle, Clock, DollarSign, Receipt } from "lucide-react";
import api from "../api";
import { uploadUrl } from "../uploadUrl";
import { K } from "../theme";
import { Bg, Bt, Cd, Sl, Inp, Tbl, Stat, Pagination, usePagination } from "./index";
import { notify, confirmDialog } from "../notify";
import { fmtDMY } from "../dateFormat";

const fmtAmt = (a) => `₹${(a || 0).toLocaleString("en-IN")}`;
const todayStr = () => new Date().toISOString().slice(0, 10);

// Add / edit form. A top-level component (not defined inside the page) so typing is not lost when the page re-renders.
const ExpenseModal = ({ cur, cats, companies, offices, onClose, onSaved }) => {
  const editing = !!cur?.id;
  const [f, setF] = useState({
    cat: cur?.catCode || "", amount: cur?.amt ? String(cur.amt) : "", date: cur?.date || todayStr(), desc: cur?.desc || "",
    company: cur?.companyId ? String(cur.companyId) : "", office: cur?.officeId ? String(cur.officeId) : "",
    paid: !!cur?.paid, paidOn: cur?.paidOn || todayStr(),
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    const amt = parseFloat(f.amount);
    if (!isFinite(amt) || amt <= 0) return setErr("Enter a valid amount");
    if (!f.desc.trim()) return setErr("Enter a description");
    if (!f.company) return setErr("Select the company");
    if (!f.office) return setErr("Select the centre");
    if (f.paid && !f.paidOn) return setErr("Select the date it was paid");
    setSaving(true); setErr("");
    try {
      const fd = new FormData();
      if (f.cat) fd.append("category_code", f.cat);
      fd.append("amount", String(amt));
      fd.append("expense_date", f.date);
      fd.append("description", f.desc.trim());
      fd.append("company_id", f.company);
      fd.append("office_id", f.office);
      fd.append("is_paid", f.paid ? "true" : "false");
      if (f.paid) fd.append("paid_on", f.paidOn);
      if (file) fd.append("receipt", file);
      await api.upload(editing ? `/api/office-expenses/${cur.id}` : "/api/office-expenses", fd, editing ? "PUT" : "POST");
      notify(editing ? "Expense updated" : "Expense added", { title: "Saved", variant: "success" });
      onSaved();
    } catch (e) { setErr(e.message || "Could not save"); } finally { setSaving(false); }
  };

  const Box = ({ on, label, icon: Ic, c, onClick }) => (
    <button type="button" onClick={onClick}
      className={`flex-1 flex items-center justify-center gap-2 px-3 py-[10px] rounded-[10px] text-[14px] font-bold cursor-pointer border-2 transition-colors ${on ? "" : "bg-bg2 border-b0 text-t2 hover:border-b1"}`}
      style={on ? { background: c + "18", borderColor: c, color: c } : undefined}>
      <Ic size={15} /> {label}
    </button>
  );

  return <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3">
    <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 sm:p-6 w-full max-w-[640px] max-h-[88vh] overflow-auto" onMouseDown={(e) => e.stopPropagation()}>
      <div className="flex justify-between mb-4">
        <span className="text-[18px] font-extrabold text-t0">{editing ? "Edit Expense" : "Add Expense"}</span>
        <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] mb-3">
        <div><div className="text-[14px] text-t2 mb-[3px]">Company <span className="text-r">*</span></div>
          <Sl options={[{ v: "", l: "— Select company —" }, ...companies.map((c) => ({ v: String(c.id), l: c.name }))]} value={f.company} onChange={(v) => set("company", v)} /></div>
        <div><div className="text-[14px] text-t2 mb-[3px]">Centre <span className="text-r">*</span></div>
          <Sl options={[{ v: "", l: "— Select centre —" }, ...offices.map((o) => ({ v: String(o.id), l: o.name || o.city }))]} value={f.office} onChange={(v) => set("office", v)} /></div>
      </div>
      <div className="mb-3"><div className="text-[14px] text-t2 mb-[3px]">Category</div>
        <Sl options={[{ v: "", l: "— Select category —" }, ...cats.map((c) => ({ v: c.cd, l: c.nm }))]} value={f.cat} onChange={(v) => set("cat", v)} /></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] mb-3">
        <div><div className="text-[14px] text-t2 mb-[3px]">Amount (₹) <span className="text-r">*</span></div><Inp type="number" placeholder="5000" value={f.amount} onChange={(v) => set("amount", v)} /></div>
        <div><div className="text-[14px] text-t2 mb-[3px]">Expense Date</div><Inp type="date" value={f.date} onChange={(v) => set("date", v)} /></div>
      </div>
      <div className="mb-3"><div className="text-[14px] text-t2 mb-[3px]">Description <span className="text-r">*</span></div>
        <textarea value={f.desc} onChange={(e) => set("desc", e.target.value)} rows={3} placeholder="What was this expense for?"
          className="w-full px-3 py-2 bg-bg2 border border-b0 rounded-[10px] text-[14px] text-t0 outline-none focus:border-p resize-y" /></div>
      <div className="mb-3">
        <div className="text-[14px] text-t2 mb-[3px]">Bill / Receipt <span className="text-t3 text-[12px]">(optional)</span></div>
        {file
          ? <div className="flex items-center gap-2 px-3 py-[6px] bg-bg2 border border-b0 rounded-[10px]">
              <FileText size={13} className="text-t3 flex-shrink-0" /><span className="text-[14px] text-t1 truncate flex-1">{file.name}</span>
              <button type="button" onClick={() => setFile(null)} className="bg-transparent border-0 text-t3 cursor-pointer p-0"><X size={14} /></button>
            </div>
          : <label className="flex items-center gap-2 px-3 py-2 bg-bg2 border border-dashed border-b1 rounded-[10px] cursor-pointer hover:border-p">
              <Upload size={13} className="text-t3 flex-shrink-0" />
              <span className="text-[14px] text-t2 truncate">{cur?.receiptUrl ? "Replace the attached bill" : "Attach PDF or image"}</span>
              <input type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); e.target.value = ""; }} />
            </label>}
        {!file && cur?.receiptUrl && <a href={uploadUrl(cur.receiptUrl)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[13px] font-semibold text-bl hover:underline mt-1"><FileText size={11} /> View current bill</a>}
      </div>
      <div className="mb-3">
        <div className="text-[14px] text-t2 mb-[3px]">Payment</div>
        <div className="flex gap-2">
          <Box on={f.paid} label="Paid" icon={CheckCircle} c={K.g} onClick={() => set("paid", true)} />
          <Box on={!f.paid} label="Unpaid" icon={Clock} c={K.a} onClick={() => set("paid", false)} />
        </div>
        {f.paid && <div className="mt-2 max-w-[220px]"><div className="text-[14px] text-t2 mb-[3px]">Paid on</div>
          <Inp type="date" value={f.paidOn} onChange={(v) => set("paidOn", v)} /></div>}
      </div>
      {err && <div className="text-[14px] text-r mb-2">{err}</div>}
      <div className="flex gap-[6px]"><Bt full onClick={submit} disabled={saving}>{saving ? "Saving..." : editing ? "Save Changes" : "Add Expense"}</Bt><Bt v="secondary" full onClick={onClose}>Cancel</Bt></div>
    </div>
  </div>;
};

export const OfficeExpenses = ({ expCats = [], can = () => true }) => {
  const canEdit = can("expenses", "ledger", null, "edit");
  const cats = useMemo(() => expCats.filter((c) => c.active !== false), [expCats]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [offices, setOffices] = useState([]);
  const [fl, setFl] = useState({ from: "", to: "", company: "all", office: "all", paid: "all" });
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null); // null | {} (new) | row (edit)

  useEffect(() => {
    api.get("/api/companies?active=true").then((d) => setCompanies(Array.isArray(d) ? d : d.data || [])).catch(() => {});
    api.get("/api/offices").then((d) => setOffices(Array.isArray(d) ? d : d.data || [])).catch(() => {});
  }, []);
  useEffect(() => { const t = setTimeout(() => setQ(search.trim()), 300); return () => clearTimeout(t); }, [search]);

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    if (fl.from) p.set("from", fl.from);
    if (fl.to) p.set("to", fl.to);
    if (fl.company !== "all") p.set("company", fl.company);
    if (fl.office !== "all") p.set("office", fl.office);
    if (fl.paid !== "all") p.set("paid", fl.paid);
    if (q) p.set("q", q);
    try { const d = await api.get("/api/office-expenses" + (p.toString() ? "?" + p.toString() : "")); setRows(Array.isArray(d) ? d : []); }
    catch (e) { notify(e.message || "Could not load expenses", { title: "Error", variant: "error" }); }
    finally { setLoading(false); }
  }, [fl, q]);
  useEffect(() => { load(); }, [load]);

  const page = usePagination(rows, 10);
  const total = rows.reduce((s, r) => s + r.amt, 0);
  const paidRows = rows.filter((r) => r.paid);
  const unpaidRows = rows.filter((r) => !r.paid);
  const sum = (a) => a.reduce((s, r) => s + r.amt, 0);

  const remove = async (r) => {
    if (!(await confirmDialog(`Delete this expense of ${fmtAmt(r.amt)}? This cannot be undone.`))) return;
    try { await api.del(`/api/office-expenses/${r.id}`); notify("Expense deleted", { title: "Deleted", variant: "success" }); load(); }
    catch (e) { notify(e.message || "Delete failed", { title: "Error", variant: "error" }); }
  };

  const reset = () => { setFl({ from: "", to: "", company: "all", office: "all", paid: "all" }); setSearch(""); };
  const filtered = fl.from || fl.to || fl.company !== "all" || fl.office !== "all" || fl.paid !== "all" || search;

  return <div>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
      <Stat icon={Receipt} label="Total Expenses" value={rows.length} sub={fmtAmt(total)} c={K.p} />
      <Stat icon={CheckCircle} label="Paid" value={fmtAmt(sum(paidRows))} sub={`${paidRows.length} expense${paidRows.length === 1 ? "" : "s"}`} c={K.g} />
      <Stat icon={Clock} label="Unpaid" value={fmtAmt(sum(unpaidRows))} sub={`${unpaidRows.length} expense${unpaidRows.length === 1 ? "" : "s"}`} c={K.a} />
      <Stat icon={DollarSign} label="Total Amount" value={fmtAmt(total)} sub="For the selected filters" c={K.bl} />
    </div>
    <div className="flex flex-wrap gap-2 mb-[10px] items-end">
      <div className="w-[240px]"><div className="text-[12px] text-t3 mb-[2px]">Search</div>
        <Inp icon={Search} placeholder="Name, description, centre, ID…" value={search} onChange={setSearch} /></div>
      <div><div className="text-[12px] text-t3 mb-[2px]">From</div><Inp type="date" value={fl.from} onChange={(v) => setFl((p) => ({ ...p, from: v }))} style={{ width: 140 }} /></div>
      <div><div className="text-[12px] text-t3 mb-[2px]">To</div><Inp type="date" value={fl.to} onChange={(v) => setFl((p) => ({ ...p, to: v }))} style={{ width: 140 }} /></div>
      <div><div className="text-[12px] text-t3 mb-[2px]">Company</div><Sl w={160} options={[{ v: "all", l: "All Companies" }, ...companies.map((c) => ({ v: String(c.id), l: c.name }))]} value={fl.company} onChange={(v) => setFl((p) => ({ ...p, company: v }))} /></div>
      <div><div className="text-[12px] text-t3 mb-[2px]">Centre</div><Sl w={160} options={[{ v: "all", l: "All Centres" }, ...offices.map((o) => ({ v: String(o.id), l: o.name || o.city }))]} value={fl.office} onChange={(v) => setFl((p) => ({ ...p, office: v }))} /></div>
      <div><div className="text-[12px] text-t3 mb-[2px]">Payment</div><Sl w={130} options={[{ v: "all", l: "All" }, { v: "paid", l: "Paid" }, { v: "unpaid", l: "Unpaid" }]} value={fl.paid} onChange={(v) => setFl((p) => ({ ...p, paid: v }))} /></div>
      {filtered && <Bt v="ghost" s="sm" onClick={reset}>Reset</Bt>}
      {canEdit && <div className="ml-auto"><Bt icon={Plus} s="sm" onClick={() => setModal({})}>Add Expense</Bt></div>}
    </div>
    {loading
      ? <div className="text-center p-[30px] text-t3 text-[14px]">Loading…</div>
      : rows.length === 0
        ? <div className="text-center p-[30px] text-t3 text-[14px] bg-bg1 border border-b0 rounded-2xl">{filtered ? "No expenses match these filters" : "No expenses recorded yet"}</div>
        : <div className="rounded-2xl border border-b0 overflow-hidden shadow-card">
            <Tbl bare cols={[
              { k: "d", l: "Date", r: (r) => <span className="text-[14px] text-t1">{fmtDMY(r.date)}</span> },
              { k: "c", l: "Company / Centre", r: (r) => <div><div className="text-[14px] font-bold text-t0">{r.office || "—"}</div><div className="text-[12px] text-t3">{r.company || "—"}</div></div> },
              { k: "cat", l: "Category", r: (r) => r.cat ? <Bg c={K.p} sm>{r.cat}</Bg> : <span className="text-t3">—</span> },
              { k: "ds", l: "Description", r: (r) => <div className="max-w-[280px]"><div className="text-[14px] text-t1 truncate" title={r.desc}>{r.desc}</div><div className="text-[12px] text-t3">Added by {r.by || "—"}</div></div> },
              { k: "a", l: "Amount", r: (r) => <span className="font-bold">{fmtAmt(r.amt)}</span> },
              { k: "p", l: "Payment", r: (r) => r.paid ? <div><Bg c={K.g} sm>Paid</Bg><div className="text-[12px] text-t3 mt-[2px]">{r.paidOn ? fmtDMY(r.paidOn) : ""}</div></div> : <Bg c={K.a} sm>Unpaid</Bg> },
              { k: "b", l: "Bill", r: (r) => r.receiptUrl ? <a href={uploadUrl(r.receiptUrl)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[13px] font-semibold text-bl hover:underline"><FileText size={11} /> View</a> : <span className="text-t3">—</span> },
              { k: "x", l: "", r: (r) => canEdit ? <div className="flex gap-1"><Bt v="ghost" s="xs" icon={Edit} onClick={() => setModal(r)}>Edit</Bt><Bt v="ghost" s="xs" icon={Trash2} onClick={() => remove(r)}>Delete</Bt></div> : null },
            ]} data={page.pageData} />
            <Pagination total={rows.length} page={page.page} pageSize={page.pageSize} onPageChange={page.onPageChange} onPageSizeChange={page.onPageSizeChange} />
          </div>}
    {modal && <ExpenseModal cur={modal} cats={cats} companies={companies} offices={offices} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
  </div>;
};
